import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  ChevronRight, ChevronLeft, Check, Building2, 
  MapPin, Shield, Package, CreditCard,
  Home, Upload, Camera, FileText, AlertTriangle,
  Loader2, CheckCircle2, XCircle, Clock, Info, X, Image
} from 'lucide-react';

const API_URL = getApiBaseUrl();
const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

// Workflow steps - each step must be completed before proceeding
const STEPS = [
  { id: 1, name: 'Details', fullName: 'Property Details', icon: Building2 },
  { id: 2, name: 'Photos', fullName: 'Photos & Media', icon: Camera },
  { id: 3, name: 'Compliance', fullName: 'Compliance', icon: Shield },
  { id: 4, name: 'Platforms', fullName: 'Platforms', icon: MapPin },
  { id: 5, name: 'Add-ons', fullName: 'Add-ons', icon: Package },
  { id: 6, name: 'Review', fullName: 'Review & Pay', icon: CreditCard },
];

export default function PropertyWizard() {
  const navigate = useNavigate();
  const { propertyId: editPropertyId } = useParams();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  
  // Property data
  const [propertyId, setPropertyId] = useState(editPropertyId || null);
  const [property, setProperty] = useState({
    address: { line1: '', line2: '', city: '', county: '', postcode: '' },
    property_type: 'flat',
    bedrooms: 1,
    bathrooms: 1,
    rent_amount: '',
    deposit_amount: '',
    description: '',
    furnished: false,
    has_gas_supply: true,
    pets_allowed: false,
    smoking_allowed: false,
    jurisdiction: 'england'
  });
  
  // Photos
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Compliance
  const [complianceRequirements, setComplianceRequirements] = useState([]);
  const [certificates, setCertificates] = useState({});
  const [uploadingCert, setUploadingCert] = useState(null);
  
  // Platforms & Add-ons
  const [platforms, setPlatforms] = useState([]);
  const [addons, setAddons] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState(['letwise_direct']);
  const [selectedAddons, setSelectedAddons] = useState([]);
  
  // Quote & Payment
  const [quote, setQuote] = useState(null);
  const [paymentComplete, setPaymentComplete] = useState(false);

  // Load initial data
  useEffect(() => {
    loadOptions();
    if (editPropertyId) {
      loadExistingProperty(editPropertyId);
    }
  }, [editPropertyId]);

  // Auto-detect jurisdiction based on postcode
  useEffect(() => {
    const postcode = property.address.postcode?.toUpperCase()?.trim() || '';
    if (!postcode || postcode.length < 2) return;
    
    let detectedJurisdiction = 'england';
    
    // Scottish postcodes start with these prefixes
    const scottishPrefixes = ['AB', 'DD', 'DG', 'EH', 'FK', 'G1', 'G2', 'G3', 'G4', 'G5', 'G6', 'G7', 'G8', 'G9', 'HS', 'IV', 'KA', 'KW', 'KY', 'ML', 'PA', 'PH', 'TD', 'ZE'];
    // Also check for G postcodes without numbers (G1-G9 handled above)
    const isGlasgowPostcode = /^G[0-9]/.test(postcode);
    
    // Welsh postcodes
    const welshPrefixes = ['CF', 'LD', 'LL', 'NP', 'SA', 'SY'];
    
    if (scottishPrefixes.some(p => postcode.startsWith(p)) || isGlasgowPostcode) {
      detectedJurisdiction = 'scotland';
    } else if (welshPrefixes.some(p => postcode.startsWith(p))) {
      detectedJurisdiction = 'wales';
    }
    
    // Only update if different to avoid infinite loops
    if (detectedJurisdiction !== property.jurisdiction) {
      setProperty(prev => ({ ...prev, jurisdiction: detectedJurisdiction }));
    }
  }, [property.address.postcode, property.jurisdiction]);

  const loadOptions = async () => {
    try {
      const [platformsRes, addonsRes] = await Promise.all([
        fetch(`${API_URL}/api/listing-workflow/platforms`),
        fetch(`${API_URL}/api/listing-workflow/addons`)
      ]);
      
      if (platformsRes.ok) {
        const data = await platformsRes.json();
        setPlatforms(data.platforms || []);
      }
      if (addonsRes.ok) {
        const data = await addonsRes.json();
        setAddons(data.addons || []);
      }
    } catch (error) {
      console.error('Failed to load options:', error);
    }
  };

  const loadExistingProperty = async (propId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/properties/${propId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Transform address keys from backend format (line_1 -> line1)
        const transformedData = {
          ...data,
          address: {
            line1: data.address?.line_1 || data.address?.line1 || '',
            line2: data.address?.line_2 || data.address?.line2 || '',
            city: data.address?.city || '',
            county: data.address?.county || '',
            postcode: data.address?.postcode || ''
          }
        };
        
        setProperty(transformedData);
        setPropertyId(propId);
        
        // Load compliance requirements
        await loadComplianceRequirements(propId);
        
        // Load existing photos
        if (data.photos?.length > 0) {
          setPhotos(data.photos);
        }
      }
    } catch (error) {
      console.error('Failed to load property:', error);
      toast.error('Failed to load property');
    } finally {
      setLoading(false);
    }
  };

  const loadComplianceRequirements = async (propId) => {
    try {
      const response = await fetch(`${API_URL}/api/listing-workflow/properties/${propId}/compliance`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setComplianceRequirements(data.requirements || []);
      }
    } catch (error) {
      console.error('Failed to load compliance:', error);
    }
  };

  const handlePropertyChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setProperty(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setProperty(prev => ({ ...prev, [field]: value }));
    }
  };

  // === STEP VALIDATIONS ===
  
  const validateStep1 = () => {
    const errors = [];
    
    if (!property.address.line1?.trim()) errors.push('Address line 1 is required');
    if (!property.address.city?.trim()) errors.push('City is required');
    if (!property.address.postcode?.trim()) errors.push('Postcode is required');
    if (!property.rent_amount || property.rent_amount <= 0) errors.push('Valid rent amount is required');
    if (!property.description?.trim() || property.description.length < 50) {
      errors.push('Description must be at least 50 characters');
    }
    
    if (errors.length > 0) {
      errors.forEach(err => toast.error(err));
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (photos.length < 3) {
      toast.error('Please upload at least 3 photos');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    const requiredCerts = complianceRequirements.filter(r => r.required);
    const missingCerts = requiredCerts.filter(r => r.status === 'missing' || r.status === 'expired');
    
    if (missingCerts.length > 0) {
      toast.error(`Missing required certificates: ${missingCerts.map(c => c.name).join(', ')}`);
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform');
      return false;
    }
    return true;
  };

  const validateStep5 = () => {
    // Add-ons are optional
    return true;
  };

  const validateStep6 = () => {
    if (!quote) {
      toast.error('Please generate a quote first');
      return false;
    }
    return true;
  };

  // === STEP ACTIONS ===

  const saveStep1 = async () => {
    if (!validateStep1()) return false;
    
    setSaving(true);
    try {
      const url = propertyId 
        ? `${API_URL}/api/properties/${propertyId}`
        : `${API_URL}/api/properties`;
      
      // Transform address keys for backend compatibility (line1 -> line_1)
      const transformedProperty = {
        ...property,
        address: {
          line_1: property.address.line1,
          line_2: property.address.line2 || '',
          city: property.address.city,
          county: property.address.county || '',
          postcode: property.address.postcode
        },
        listing_status: 'draft'
      };
      
      const response = await fetch(url, {
        method: propertyId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transformedProperty)
      });
      
      if (response.ok) {
        const data = await response.json();
        const newPropertyId = data.id || propertyId;
        setPropertyId(newPropertyId);
        
        // Load compliance requirements for this property
        await loadComplianceRequirements(newPropertyId);
        
        toast.success('Property details saved');
        return true;
      } else {
        const error = await response.json();
        // Handle Pydantic validation errors
        if (Array.isArray(error.detail)) {
          const messages = error.detail.map(e => e.msg || e.message || 'Validation error').join(', ');
          toast.error(messages);
        } else if (typeof error.detail === 'object' && error.detail.msg) {
          toast.error(error.detail.msg);
        } else {
          toast.error(error.detail || error.message || 'Failed to save property');
        }
        return false;
      }
    } catch (error) {
      toast.error('Failed to save property');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    if (!propertyId) {
      toast.error('Please save property details first');
      return;
    }
    
    setUploadingPhoto(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API_URL}/api/properties/${propertyId}/photos`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getToken()}` },
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          setPhotos(prev => [...prev, { id: data.id, url: data.url, filename: file.name }]);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      } catch (error) {
        toast.error(`Error uploading ${file.name}`);
      }
    }
    
    setUploadingPhoto(false);
    event.target.value = '';
  };

  const removePhoto = async (photoId) => {
    try {
      await fetch(`${API_URL}/api/properties/${propertyId}/photos/${photoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  const uploadCertificate = async (certType, certNumber, expiryDate) => {
    if (!propertyId) {
      toast.error('Please save property details first');
      return;
    }
    
    setUploadingCert(certType);
    try {
      const response = await fetch(
        `${API_URL}/api/listing-workflow/properties/${propertyId}/certificates/${certType}?certificate_number=${encodeURIComponent(certNumber || '')}&expiry_date=${encodeURIComponent(expiryDate || '')}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getToken()}` }
        }
      );
      
      if (response.ok) {
        toast.success('Certificate uploaded');
        await loadComplianceRequirements(propertyId);
        return true;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload certificate');
        return false;
      }
    } catch (error) {
      toast.error('Failed to upload certificate');
      return false;
    } finally {
      setUploadingCert(null);
    }
  };

  const savePlatforms = async () => {
    if (!validateStep4()) return false;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/listing-workflow/properties/${propertyId}/platforms`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ platforms: selectedPlatforms })
      });
      
      if (response.ok) {
        toast.success('Platforms saved');
        return true;
      }
      return false;
    } catch (error) {
      toast.error('Failed to save platforms');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAddons = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/listing-workflow/properties/${propertyId}/addons`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ addons: selectedAddons })
      });
      
      if (response.ok) {
        // Generate quote after saving add-ons
        await generateQuote();
        return true;
      }
      return false;
    } catch (error) {
      toast.error('Failed to save add-ons');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const generateQuote = async () => {
    try {
      const response = await fetch(`${API_URL}/api/listing-workflow/properties/${propertyId}/quote`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setQuote(data);
        return true;
      }
      return false;
    } catch (error) {
      toast.error('Failed to generate quote');
      return false;
    }
  };

  const processPayment = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/listing-workflow/properties/${propertyId}/payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ payment_method: 'card' })
      });
      
      if (response.ok) {
        setPaymentComplete(true);
        toast.success('Payment processed');
        return true;
      }
      return false;
    } catch (error) {
      toast.error('Payment failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goLive = async () => {
    // Final validation of ALL steps
    if (!completedSteps.has(1) || !completedSteps.has(2) || !completedSteps.has(3) || 
        !completedSteps.has(4) || !completedSteps.has(5)) {
      toast.error('Please complete all steps before going live');
      return;
    }
    
    if (!paymentComplete && quote?.total > 0) {
      toast.error('Please complete payment first');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/listing-workflow/properties/${propertyId}/go-live`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        toast.success('Your property is now LIVE! 🎉');
        navigate('/landlord/properties');
      } else {
        const error = await response.json();
        toast.error(error.detail?.error || 'Cannot go live yet');
      }
    } catch (error) {
      toast.error('Failed to publish listing');
    } finally {
      setSaving(false);
    }
  };

  // === NAVIGATION ===

  const handleNext = async () => {
    let success = false;
    
    switch (currentStep) {
      case 1:
        success = await saveStep1();
        break;
      case 2:
        success = validateStep2();
        break;
      case 3:
        success = validateStep3();
        break;
      case 4:
        success = await savePlatforms();
        break;
      case 5:
        success = await saveAddons();
        break;
      case 6:
        // Final step - go live
        await goLive();
        return;
      default:
        success = true;
    }
    
    if (success) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(prev => Math.min(prev + 1, 6));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const canNavigateToStep = (step) => {
    // Can always go back to completed steps
    if (completedSteps.has(step)) return true;
    // Can only go to next step if current step is completed
    if (step === currentStep) return true;
    if (step === currentStep + 1 && completedSteps.has(currentStep)) return true;
    return false;
  };

  // === RENDER STEPS ===

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1.5">Address Line 1 *</label>
          <input
            type="text"
            value={property.address.line1}
            onChange={(e) => handlePropertyChange('address.line1', e.target.value)}
            className="w-full px-4 py-3 text-base border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="123 Example Street"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1.5">Address Line 2</label>
          <input
            type="text"
            value={property.address.line2}
            onChange={(e) => handlePropertyChange('address.line2', e.target.value)}
            className="w-full px-4 py-3 text-base border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Flat 4A"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1.5">City *</label>
            <input
              type="text"
              value={property.address.city}
              onChange={(e) => handlePropertyChange('address.city', e.target.value)}
              className="w-full px-4 py-3 text-base border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Edinburgh"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Postcode *</label>
            <input
              type="text"
              value={property.address.postcode}
              onChange={(e) => handlePropertyChange('address.postcode', e.target.value.toUpperCase())}
              className="w-full px-4 py-3 text-base border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="EH1 1AA"
            />
          </div>
        </div>
      </div>
      
      {/* Jurisdiction indicator */}
      <div className={`p-3 rounded-xl text-sm ${
        property.jurisdiction === 'scotland' ? 'bg-blue-50 border border-blue-200 text-blue-800' :
        property.jurisdiction === 'wales' ? 'bg-red-50 border border-red-200 text-red-800' :
        'bg-slate-50 border border-slate-200 text-slate-700'
      }`}>
        <span className="font-medium">Jurisdiction: </span>
        <span className="capitalize">{property.jurisdiction}</span>
        {property.jurisdiction === 'scotland' && ' (PRT applies)'}
        {property.jurisdiction === 'wales' && ' (Rent Smart Wales applies)'}
        {property.jurisdiction === 'england' && ' (AST applies)'}
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1.5">Property Type *</label>
          <select
            value={property.property_type}
            onChange={(e) => handlePropertyChange('property_type', e.target.value)}
            className="w-full px-4 py-3 text-base border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="flat">Flat</option>
            <option value="house">House</option>
            <option value="studio">Studio</option>
            <option value="room">Room</option>
            <option value="hmo">HMO</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">Bedrooms</label>
          <input
            type="number"
            min="0"
            value={property.bedrooms}
            onChange={(e) => handlePropertyChange('bedrooms', parseInt(e.target.value) || 0)}
            className="w-full px-4 py-3 text-base border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1.5">Monthly Rent (£) *</label>
          <input
            type="number"
            value={property.rent_amount}
            onChange={(e) => handlePropertyChange('rent_amount', parseFloat(e.target.value) || '')}
            className="w-full px-4 py-3 text-base border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="1200"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">Deposit (£)</label>
          <input
            type="number"
            value={property.deposit_amount}
            onChange={(e) => handlePropertyChange('deposit_amount', parseFloat(e.target.value) || '')}
            className="w-full px-4 py-3 text-base border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder={String((property.rent_amount || 0) * (property.jurisdiction === 'scotland' ? 2 : 5))}
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-1.5">Description * (min 50 chars)</label>
        <textarea
          value={property.description}
          onChange={(e) => handlePropertyChange('description', e.target.value)}
          className="w-full px-4 py-3 text-base border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          rows={4}
          placeholder="Describe your property..."
        />
        <p className={`text-xs mt-1 ${property.description.length >= 50 ? 'text-green-600' : 'text-amber-600'}`}>
          {property.description.length}/50 characters
        </p>
      </div>
      
      <div className="flex flex-wrap gap-3">
        {[
          { key: 'furnished', label: 'Furnished' },
          { key: 'pets_allowed', label: 'Pets OK' },
          { key: 'has_gas_supply', label: 'Has Gas' },
        ].map(({ key, label }) => (
          <label key={key} className="flex items-center gap-2 px-4 py-2.5 border rounded-xl cursor-pointer hover:bg-slate-50">
            <input
              type="checkbox"
              checked={property[key]}
              onChange={(e) => handlePropertyChange(key, e.target.checked)}
              className="w-5 h-5 text-primary rounded"
            />
            <span className="text-sm font-medium">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className={`p-4 rounded-xl border ${photos.length >= 3 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {photos.length >= 3 ? (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            )}
            <span className="font-medium">{photos.length}/3 photos uploaded</span>
          </div>
          {photos.length < 3 && (
            <span className="text-sm text-amber-600">{3 - photos.length} more required</span>
          )}
        </div>
      </div>
      
      {/* Photo Grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {photos.map((photo, index) => (
            <div key={photo.id || index} className="relative aspect-video bg-slate-100 rounded-xl overflow-hidden">
              <img 
                src={photo.url || '/placeholder.jpg'} 
                alt={`Property photo ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => removePhoto(photo.id)}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600"
              >
                <X className="w-4 h-4" />
              </button>
              {index === 0 && (
                <span className="absolute bottom-2 left-2 px-2 py-1 bg-primary text-white text-xs rounded-lg">
                  Main Photo
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Upload Area */}
      <div 
        onClick={() => fileInputRef.current?.click()}
        className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer"
      >
        {uploadingPhoto ? (
          <Loader2 className="w-10 h-10 text-primary mx-auto mb-3 animate-spin" />
        ) : (
          <Camera className="w-10 h-10 text-slate-400 mx-auto mb-3" />
        )}
        <p className="font-medium text-slate-700">
          {uploadingPhoto ? 'Uploading...' : 'Tap to add photos'}
        </p>
        <p className="text-sm text-slate-500 mt-1">JPG, PNG up to 10MB</p>
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handlePhotoUpload}
        className="hidden"
      />
      
      <div className="p-4 bg-slate-50 rounded-xl">
        <p className="font-medium text-sm mb-2">Photo Requirements:</p>
        <ul className="text-sm text-slate-600 space-y-1">
          <li className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" /> Minimum 3 photos required
          </li>
          <li className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" /> Include all main rooms
          </li>
          <li className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" /> Good lighting preferred
          </li>
        </ul>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      {complianceRequirements.length === 0 ? (
        <div className="p-6 bg-slate-50 rounded-xl text-center">
          <Loader2 className="w-8 h-8 text-primary mx-auto mb-3 animate-spin" />
          <p className="text-slate-600">Loading compliance requirements...</p>
        </div>
      ) : (
        <>
          <div className="p-4 bg-slate-50 rounded-xl">
            <p className="font-medium text-sm">
              Required certificates for <span className="capitalize">{property.jurisdiction}</span> properties:
            </p>
          </div>
          
          <div className="space-y-3">
            {complianceRequirements.map((req) => (
              <Card key={req.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-sm">{req.name}</h3>
                      {req.required && (
                        <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Required</span>
                      )}
                    </div>
                    
                    <div className="mt-2">
                      {req.status === 'valid' && (
                        <div className="flex items-center gap-2 text-green-600 text-sm">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Valid</span>
                        </div>
                      )}
                      {req.status === 'expiring_soon' && (
                        <div className="flex items-center gap-2 text-amber-600 text-sm">
                          <AlertTriangle className="w-4 h-4" />
                          <span>Expiring soon</span>
                        </div>
                      )}
                      {req.status === 'expired' && (
                        <div className="flex items-center gap-2 text-red-600 text-sm">
                          <XCircle className="w-4 h-4" />
                          <span>Expired</span>
                        </div>
                      )}
                      {req.status === 'missing' && (
                        <CertificateUploadForm
                          certType={req.requirement?.document_type || req.id}
                          onUpload={uploadCertificate}
                          uploading={uploadingCert === (req.requirement?.document_type || req.id)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const renderStep4 = () => {
    // Calculate platform costs for preview
    const platformCosts = selectedPlatforms.reduce((total, pid) => {
      const p = platforms.find(pl => pl.id === pid);
      return total + (p?.price_monthly || 0);
    }, 0);

    return (
      <div className="space-y-4">
        <div className="p-4 bg-slate-50 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Selected Portals</p>
              <p className="text-xs text-slate-500">{selectedPlatforms.length} platform(s)</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-primary">
                £{platformCosts.toFixed(2)}<span className="text-sm font-normal text-slate-500">/mo</span>
              </p>
              {platformCosts > 0 && (
                <p className="text-xs text-slate-500">Deducted from rent</p>
              )}
            </div>
          </div>
        </div>
        
        <p className="text-sm text-slate-600">Select where you want your property advertised:</p>
        
        <div className="space-y-3">
          {platforms.map((platform) => (
            <Card
              key={platform.id}
              className={`p-4 cursor-pointer transition-all ${
                selectedPlatforms.includes(platform.id)
                  ? 'ring-2 ring-primary border-primary bg-primary/5'
                  : 'hover:border-slate-300'
              } ${platform.included ? 'bg-green-50 border-green-200' : ''}`}
              onClick={() => {
                if (!platform.included) {
                  setSelectedPlatforms(prev =>
                    prev.includes(platform.id)
                      ? prev.filter(p => p !== platform.id)
                      : [...prev, platform.id]
                  );
                }
              }}
            >
              <div className="flex items-start gap-4">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                  selectedPlatforms.includes(platform.id) 
                    ? 'bg-primary border-primary' 
                    : 'border-slate-300'
                }`}>
                  {selectedPlatforms.includes(platform.id) && (
                    <Check className="w-4 h-4 text-white" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold">{platform.name}</h3>
                    {platform.included && (
                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Included Free</span>
                    )}
                    {platform.reach && (
                      <span className="text-xs text-slate-500">{platform.reach}</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{platform.description}</p>
                  
                  {platform.features && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {platform.features.slice(0, 3).map((feature, i) => (
                        <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                          {feature}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="text-right flex-shrink-0">
                  {platform.price_monthly === 0 ? (
                    <span className="text-green-600 font-bold">FREE</span>
                  ) : (
                    <>
                      <p className="font-bold text-primary">£{platform.price_monthly}</p>
                      <p className="text-xs text-slate-500">/month</p>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
        
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
          <Info className="w-4 h-4 inline mr-1" />
          Portal fees are deducted monthly from your rent payouts
        </div>
      </div>
    );
  };

  const renderStep5 = () => {
    // Group addons by category
    const addonCategories = {
      services: { label: 'As a Service', items: [] },
      insurance: { label: 'Insurance & Protection', items: [] },
      one_time: { label: 'One-Time Services', items: [] },
      compliance: { label: 'Compliance Certificates', items: [] },
    };
    
    addons.forEach(addon => {
      const cat = addon.category || 'one_time';
      if (addonCategories[cat]) {
        addonCategories[cat].items.push(addon);
      }
    });
    
    // Calculate addon costs
    const monthlyAddonCost = selectedAddons.reduce((total, aid) => {
      const a = addons.find(ad => ad.id === aid);
      return total + (a?.price_monthly || 0);
    }, 0);
    
    const setupAddonCost = selectedAddons.reduce((total, aid) => {
      const a = addons.find(ad => ad.id === aid);
      return total + (a?.price_setup || 0);
    }, 0);

    return (
      <div className="space-y-4">
        {/* Cost Summary */}
        <div className="p-4 bg-slate-50 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Selected Add-ons</p>
              <p className="text-xs text-slate-500">{selectedAddons.length} service(s)</p>
            </div>
            <div className="text-right">
              {monthlyAddonCost > 0 && (
                <p className="text-lg font-bold text-primary">
                  £{monthlyAddonCost.toFixed(2)}<span className="text-sm font-normal text-slate-500">/mo</span>
                </p>
              )}
              {setupAddonCost > 0 && (
                <p className="text-sm text-slate-600">
                  + £{setupAddonCost.toFixed(2)} setup
                </p>
              )}
              {monthlyAddonCost === 0 && setupAddonCost === 0 && (
                <p className="text-slate-500">No add-ons selected</p>
              )}
            </div>
          </div>
        </div>
        
        <p className="text-sm text-slate-600">Optional services to enhance your listing and simplify management:</p>
        
        {addons.length === 0 ? (
          <div className="p-6 bg-slate-50 rounded-xl text-center">
            <Loader2 className="w-6 h-6 text-primary mx-auto mb-2 animate-spin" />
            <p className="text-slate-500">Loading add-ons...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(addonCategories).map(([catKey, category]) => {
              if (category.items.length === 0) return null;
              
              return (
                <div key={catKey}>
                  <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                    {catKey === 'services' && <Package className="w-4 h-4" />}
                    {catKey === 'insurance' && <Shield className="w-4 h-4" />}
                    {catKey === 'one_time' && <Camera className="w-4 h-4" />}
                    {catKey === 'compliance' && <FileText className="w-4 h-4" />}
                    {category.label}
                  </h3>
                  
                  <div className="space-y-3">
                    {category.items.map((addon) => (
                      <Card
                        key={addon.id}
                        className={`p-4 cursor-pointer transition-all ${
                          selectedAddons.includes(addon.id) 
                            ? 'ring-2 ring-primary border-primary bg-primary/5' 
                            : 'hover:border-slate-300'
                        }`}
                        onClick={() => {
                          setSelectedAddons(prev =>
                            prev.includes(addon.id)
                              ? prev.filter(a => a !== addon.id)
                              : [...prev, addon.id]
                          );
                        }}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                            selectedAddons.includes(addon.id) 
                              ? 'bg-primary border-primary' 
                              : 'border-slate-300'
                          }`}>
                            {selectedAddons.includes(addon.id) && (
                              <Check className="w-4 h-4 text-white" />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm">{addon.name}</h4>
                            <p className="text-sm text-slate-600 mt-0.5">{addon.description}</p>
                            
                            {addon.features && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {addon.features.slice(0, 4).map((feature, i) => (
                                  <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                                    ✓ {feature}
                                  </span>
                                ))}
                              </div>
                            )}
                            
                            {addon.savings && (
                              <p className="text-xs text-green-600 mt-2 font-medium">{addon.savings}</p>
                            )}
                          </div>
                          
                          <div className="text-right flex-shrink-0">
                            {addon.price_monthly > 0 ? (
                              <>
                                <p className="font-bold text-primary">£{addon.price_monthly}</p>
                                <p className="text-xs text-slate-500">/month</p>
                              </>
                            ) : addon.price_per_viewing > 0 ? (
                              <>
                                <p className="font-bold text-primary">£{addon.price_per_viewing}</p>
                                <p className="text-xs text-slate-500">/viewing</p>
                                {addon.price_setup > 0 && (
                                  <p className="text-xs text-slate-400">+ £{addon.price_setup} setup</p>
                                )}
                              </>
                            ) : addon.price_setup > 0 ? (
                              <>
                                <p className="font-bold text-primary">£{addon.price_setup}</p>
                                <p className="text-xs text-slate-500">one-time</p>
                              </>
                            ) : (
                              <span className="text-green-600 font-bold">FREE</span>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 inline mr-1" />
          Monthly costs will be automatically deducted from your rent payouts
        </div>
      </div>
    );
  };

  const renderStep6 = () => {
    // Build basket items from quote
    const basketItems = quote?.items || [];
    const monthlyItems = basketItems.filter(i => i.recurring || i.price_monthly > 0);
    const setupItems = basketItems.filter(i => !i.recurring && i.price_setup > 0);
    
    return (
      <div className="space-y-4">
        {/* Property Summary */}
        <Card className="p-4">
          <h3 className="font-bold mb-3 flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Property Summary
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Address</span>
              <span className="font-medium text-right">{property.address.line1}, {property.address.city}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Monthly Rent</span>
              <span className="font-medium">£{property.rent_amount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Photos</span>
              <span className="font-medium">{photos.length} uploaded</span>
            </div>
          </div>
        </Card>

        {/* Basket Breakdown */}
        {quote ? (
          <>
            {/* Selected Platforms */}
            <Card className="p-4">
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Selected Portals ({selectedPlatforms.length})
              </h3>
              <div className="space-y-2">
                {basketItems.filter(i => i.item_type === 'platform').map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <span className="font-medium">{item.name}</span>
                      {item.price_monthly === 0 && (
                        <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">FREE</span>
                      )}
                    </div>
                    <span className={item.price_monthly > 0 ? 'font-medium' : 'text-green-600'}>
                      {item.price_monthly > 0 ? `£${item.price_monthly.toFixed(2)}/mo` : 'Included'}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Selected Add-ons */}
            {basketItems.filter(i => i.item_type === 'addon').length > 0 && (
              <Card className="p-4">
                <h3 className="font-bold mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Selected Add-ons ({selectedAddons.length})
                </h3>
                <div className="space-y-2">
                  {basketItems.filter(i => i.item_type === 'addon').map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <span className="font-medium">{item.name}</span>
                        <span className="ml-2 text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded capitalize">
                          {item.category?.replace('_', ' ')}
                        </span>
                      </div>
                      <span className="font-medium">
                        {item.price_monthly > 0 
                          ? `£${item.price_monthly.toFixed(2)}/mo` 
                          : `£${item.price_setup?.toFixed(2)}`
                        }
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Payment Breakdown */}
            <Card className="p-4 bg-slate-50">
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Payment Breakdown
              </h3>
              
              <div className="space-y-3">
                {/* Monthly Costs */}
                {quote.monthly_subtotal > 0 && (
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Monthly Recurring</p>
                    <div className="flex justify-between text-sm">
                      <span>Subtotal</span>
                      <span>£{quote.monthly_subtotal?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-slate-500">
                      <span>VAT (20%)</span>
                      <span>£{quote.monthly_vat?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold pt-2 border-t mt-2">
                      <span>Monthly Total</span>
                      <span className="text-primary">£{quote.monthly_total?.toFixed(2)}/mo</span>
                    </div>
                  </div>
                )}
                
                {/* One-time Setup */}
                {quote.setup_subtotal > 0 && (
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">One-Time Setup</p>
                    <div className="flex justify-between text-sm">
                      <span>Subtotal</span>
                      <span>£{quote.setup_subtotal?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-slate-500">
                      <span>VAT (20%)</span>
                      <span>£{quote.setup_vat?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold pt-2 border-t mt-2">
                      <span>Setup Total</span>
                      <span>£{quote.setup_total?.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                
                {/* First Payment Total */}
                <div className="p-4 bg-primary/10 rounded-lg border-2 border-primary">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-lg">Pay Today</p>
                      <p className="text-xs text-slate-600">Setup + first month</p>
                    </div>
                    <p className="text-2xl font-bold text-primary">
                      £{quote.first_payment?.toFixed(2) || quote.total?.toFixed(2)}
                    </p>
                  </div>
                </div>
                
                {/* Monthly Deduction Notice */}
                {quote.monthly_total > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <p className="font-medium text-amber-800 mb-1">Monthly Payout Deduction</p>
                    <p className="text-amber-700">
                      £{quote.monthly_total?.toFixed(2)} will be deducted from your rent payouts each month
                    </p>
                    <p className="text-amber-600 text-xs mt-1">
                      Net monthly rent payout: £{(property.rent_amount - (quote.monthly_total || 0)).toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Payment Button */}
              {!paymentComplete && quote.first_payment > 0 && (
                <Button 
                  className="w-full mt-4"
                  onClick={processPayment}
                  disabled={saving}
                  size="lg"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                  ) : (
                    <><CreditCard className="w-4 h-4 mr-2" /> Pay £{quote.first_payment?.toFixed(2) || quote.total?.toFixed(2)}</>
                  )}
                </Button>
              )}
              
              {(paymentComplete || (quote.first_payment === 0 && quote.total === 0)) && (
                <div className="mt-4 p-3 bg-green-100 border border-green-300 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">
                      {quote.first_payment === 0 ? 'No payment required - Free listing!' : 'Payment complete'}
                    </span>
                  </div>
                </div>
              )}
            </Card>
          </>
        ) : (
          <div className="p-6 bg-slate-50 rounded-xl text-center">
            <Loader2 className="w-8 h-8 text-primary mx-auto mb-3 animate-spin" />
            <p className="text-slate-600">Generating your quote...</p>
          </div>
        )}
        
        {/* Pre-flight Checklist */}
        <Card className="p-4">
          <h3 className="font-bold mb-3 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Pre-Flight Checklist
          </h3>
          <div className="space-y-2">
            {[
              { check: completedSteps.has(1), label: 'Property details complete' },
              { check: photos.length >= 3, label: 'At least 3 photos uploaded' },
              { check: completedSteps.has(3), label: 'Compliance certificates verified' },
              { check: selectedPlatforms.length > 0, label: 'Distribution platforms selected' },
              { check: paymentComplete || (quote && (quote.first_payment === 0 || quote.total === 0)), label: 'Payment processed' },
            ].map((item, i) => (
              <div key={i} className={`flex items-center gap-2 text-sm ${item.check ? 'text-green-600' : 'text-slate-400'}`}>
                {item.check ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const currentStepData = STEPS.find(s => s.id === currentStep);

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto pb-24">
        {/* Mobile Step Indicator */}
        <div className="mb-6">
          {/* Progress bar */}
          <div className="flex items-center gap-1 mb-4">
            {STEPS.map((step, index) => (
              <div
                key={step.id}
                className={`flex-1 h-1.5 rounded-full transition-colors ${
                  completedSteps.has(step.id) ? 'bg-green-500' :
                  step.id === currentStep ? 'bg-primary' :
                  'bg-slate-200'
                }`}
              />
            ))}
          </div>
          
          {/* Current step label */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                completedSteps.has(currentStep) ? 'bg-green-500 text-white' : 'bg-primary text-white'
              }`}>
                {completedSteps.has(currentStep) ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <currentStepData.icon className="w-4 h-4" />
                )}
              </div>
              <div>
                <p className="text-xs text-slate-500">Step {currentStep} of {STEPS.length}</p>
                <p className="font-semibold">{currentStepData.fullName}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Step Content */}
        <Card className="p-4 sm:p-6 mb-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}
          {currentStep === 6 && renderStep6()}
        </Card>

        {/* Fixed Bottom Navigation */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg z-30">
          <div className="max-w-2xl mx-auto flex gap-3">
            {currentStep > 1 && (
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={saving}
                className="flex-1"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            
            <Button
              onClick={handleNext}
              disabled={saving || (currentStep === 1 && !property.address.line1)}
              className="flex-1"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : currentStep === 6 ? (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> Go Live</>
              ) : (
                <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

// Certificate Upload Form Component
function CertificateUploadForm({ certType, onUpload, uploading }) {
  const [certNumber, setCertNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');

  const handleSubmit = () => {
    if (!certNumber.trim()) {
      toast.error('Please enter certificate number');
      return;
    }
    if (!expiryDate) {
      toast.error('Please select expiry date');
      return;
    }
    onUpload(certType, certNumber, expiryDate);
  };

  return (
    <div className="mt-2 p-3 bg-slate-50 rounded-lg space-y-2">
      <input
        type="text"
        placeholder="Certificate number"
        value={certNumber}
        onChange={(e) => setCertNumber(e.target.value)}
        className="w-full px-3 py-2 text-sm border rounded-lg"
      />
      <input
        type="date"
        value={expiryDate}
        onChange={(e) => setExpiryDate(e.target.value)}
        className="w-full px-3 py-2 text-sm border rounded-lg"
      />
      <Button 
        size="sm" 
        onClick={handleSubmit}
        disabled={uploading}
        className="w-full"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload Certificate'}
      </Button>
    </div>
  );
}
